import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { PackageCheck, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { EditReceivingDialog } from '@/components/procurement/EditReceivingDialog';
import { ReceiveDialog } from '@/components/procurement/ReceiveDialog';
import {
  RECEIPT_STATUS_LABELS,
  receivingLineName,
  useReceivingLines,
  type ReceiptStatus,
  type ReceivingLineRow,
} from '@/features/procurement/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDate, formatUnitCurrency } from '@/lib/formatters';
import { formatQtyUom, isPackUnit } from '@/lib/uom';
import { printDocument } from '@/lib/printDocument';

function receivedUom(r: ReceivingLineRow): string {
  return r.po_lines?.uom || r.items?.base_uom || '';
}

/** "2 rolls (3,600 in)" when received in a purchase unit, else "3,600 in". */
function receivedQtyLabel(r: ReceivingLineRow): string {
  const label = formatQtyUom(r.quantity, receivedUom(r));
  if (!isPackUnit(Number(r.conversion_to_base)) || !r.items) return label;
  return `${label} (${formatQtyUom(Number(r.base_quantity), r.items.base_uom)})`;
}

function extendedCost(r: ReceivingLineRow): number | null {
  return r.unit_cost_cents == null ? null : r.unit_cost_cents * r.quantity;
}

function facetOptions(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

function printReceipt(line: ReceivingLineRow, allRows: ReceivingLineRow[]) {
  const receipt = line.goods_receipts;
  if (!receipt) return;
  const lines = allRows.filter((row) => row.goods_receipts?.id === receipt.id);
  const total = lines.reduce((sum, row) => sum + (extendedCost(row) ?? 0), 0);
  const opened = printDocument({
    title: 'Goods receipt',
    number: receipt.receipt_number,
    status: RECEIPT_STATUS_LABELS[receipt.status] ?? receipt.status,
    subtitle: receipt.purchase_orders?.vendors?.name ?? undefined,
    sections: [
      {
        title: 'Receipt details',
        fields: [
          { label: 'Received', value: formatDate(receipt.received_at) },
          { label: 'Purchase order', value: receipt.purchase_orders?.po_number },
          { label: 'Vendor', value: receipt.purchase_orders?.vendors?.name },
          { label: 'Packing slip', value: receipt.packing_slip_number },
          { label: 'Receipt value', value: formatCurrency(total) },
        ],
      },
      {
        title: 'Items received',
        table: {
          columns: [
            { label: 'SKU' },
            { label: 'Item' },
            { label: 'Location' },
            { label: 'Lot' },
            { label: 'Status' },
            { label: 'Qty', align: 'right' },
            { label: 'Unit cost', align: 'right' },
            { label: 'Total', align: 'right' },
          ],
          rows: lines.map((row) => [
            row.items?.sku ?? (row.item_id ? null : 'Custom'),
            receivingLineName(row),
            row.locations?.name,
            row.lots?.lot_number,
            RECEIPT_STATUS_LABELS[row.status] ?? row.status,
            receivedQtyLabel(row),
            formatUnitCurrency(row.unit_cost_cents),
            formatCurrency(extendedCost(row)),
          ]),
        },
      },
      ...(receipt.notes ? [{ title: 'Receipt notes', text: receipt.notes }] : []),
    ],
  });
  if (!opened) toast.error('Allow pop-ups to print or save this receipt as a PDF.');
}

/**
 * Receiving — every item checked in against a purchase order, one row per
 * received line. Posting a catalog line adds stock to on-hand (as a Receipt
 * stock movement) and logs the unit cost. A custom line is recorded as
 * arrived and is not added to inventory. The PO closes as Delivered once
 * every line is covered.
 */
export function ReceivingPage() {
  const { role } = useAuth();
  const { data, isLoading } = useReceivingLines();
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [editing, setEditing] = useState<ReceivingLineRow | null>(null);

  const canManage = canWrite(role, 'procurement');
  const rows = useMemo<ReceivingLineRow[]>(() => data ?? [], [data]);

  const stats = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = rows.filter(
      (r) =>
        r.status !== 'reversed' && new Date(r.goods_receipts?.received_at ?? 0).getTime() >= cutoff,
    );
    return {
      receipts: new Set(recent.map((r) => r.goods_receipts?.id)).size,
      units: recent.reduce((acc, r) => acc + r.quantity, 0),
      value: recent.reduce((acc, r) => acc + (extendedCost(r) ?? 0), 0),
    };
  }, [rows]);

  const columns = useMemo<ColumnDef<ReceivingLineRow>[]>(
    () => [
      {
        id: 'status',
        accessorFn: (r) => r.status ?? 'posted',
        header: 'Status',
        size: 140,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const status = (row.original.status ?? 'posted') as ReceiptStatus;
          if (status === 'posted' && row.original.goods_receipts?.status !== 'partially_reversed') {
            return <Badge variant="success">Posted</Badge>;
          }
          const label =
            status === 'reversed'
              ? RECEIPT_STATUS_LABELS.reversed
              : (RECEIPT_STATUS_LABELS[row.original.goods_receipts?.status ?? status] ?? status);
          return <Badge variant={status === 'reversed' ? 'muted' : 'warning'}>{label}</Badge>;
        },
      },
      {
        id: 'received_at',
        accessorFn: (r) => r.goods_receipts?.received_at ?? '',
        header: 'Received',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.goods_receipts?.received_at)}
          </span>
        ),
      },
      {
        id: 'receipt_number',
        accessorFn: (r) => r.goods_receipts?.receipt_number ?? '',
        header: 'Receipt #',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs">
            {row.original.goods_receipts?.receipt_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'po_number',
        accessorFn: (r) => r.goods_receipts?.purchase_orders?.po_number ?? '',
        header: 'PO #',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs">
            {row.original.goods_receipts?.purchase_orders?.po_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'vendor',
        accessorFn: (r) => r.goods_receipts?.purchase_orders?.vendors?.name ?? '',
        header: 'Vendor',
        size: 170,
        minSize: 110,
        filterFn: arrIncludesSome,
      },
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 130,
        minSize: 90,
        cell: ({ row }) =>
          row.original.item_id ? (
            <Link
              to={`/catalog/items/${row.original.item_id}?tab=procurement`}
              onClick={(e) => e.stopPropagation()}
              className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
            >
              {row.original.items?.sku ?? '—'}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'item',
        accessorFn: (r) => receivingLineName(r),
        header: 'Item',
        size: 220,
        minSize: 140,
        cell: ({ row }) => (
          <span className="font-medium">
            {receivingLineName(row.original)}
            {!row.original.item_id && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">custom</span>
            )}
          </span>
        ),
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => <span className="block text-right">Qty</span>,
        size: 100,
        minSize: 80,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {row.original.quantity.toLocaleString()}
            <span className="ml-1 text-xs text-muted-foreground">{receivedUom(row.original)}</span>
            {isPackUnit(Number(row.original.conversion_to_base)) && row.original.items && (
              <span className="block text-[11px] text-muted-foreground">
                = {formatQtyUom(Number(row.original.base_quantity), row.original.items.base_uom)}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'unit_cost',
        accessorFn: (r) => r.unit_cost_cents ?? 0,
        header: () => <span className="block text-right">Unit cost</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const cost = row.original.unit_cost_cents;
          const poPrice = row.original.po_lines?.unit_price_cents;
          const variance = cost != null && poPrice != null && Number(cost) !== Number(poPrice);
          return (
            <span className="block text-right tabular-nums">
              {formatUnitCurrency(cost)}
              {variance && (
                <span className="block text-[11px] text-muted-foreground">
                  PO {formatUnitCurrency(poPrice)}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'extended',
        accessorFn: (r) => extendedCost(r) ?? 0,
        header: () => <span className="block text-right">Ext. cost</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-muted-foreground">
            {formatCurrency(extendedCost(row.original))}
          </span>
        ),
      },
      {
        id: 'location',
        accessorFn: (r) => r.locations?.name ?? '',
        header: 'Location',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
      },
      {
        id: 'lot',
        accessorFn: (r) => r.lots?.lot_number ?? '',
        header: 'Lot',
        size: 110,
        minSize: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.lots?.lot_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'packing_slip',
        accessorFn: (r) => r.goods_receipts?.packing_slip_number ?? '',
        header: 'Packing slip',
        size: 120,
        minSize: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.goods_receipts?.packing_slip_number ?? '—'}
          </span>
        ),
      },
      {
        id: '__print',
        header: '',
        size: 48,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Print or save as PDF"
            aria-label={`Print ${row.original.goods_receipts?.receipt_number ?? 'receipt'}`}
            onClick={(event) => {
              event.stopPropagation();
              printReceipt(row.original, rows);
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [rows],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      {
        columnId: 'vendor',
        title: 'Vendor',
        options: facetOptions(rows.map((r) => r.goods_receipts?.purchase_orders?.vendors?.name)),
      },
      {
        columnId: 'location',
        title: 'Location',
        options: facetOptions(rows.map((r) => r.locations?.name)),
      },
      {
        columnId: 'status',
        title: 'Status',
        options: [
          { value: 'posted', label: 'Posted' },
          { value: 'reversed', label: 'Reversed' },
        ],
      },
    ],
    [rows],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Receiving"
        actions={
          canManage ? (
            <Button onClick={() => setReceiveOpen(true)}>
              <PackageCheck className="h-4 w-4" />
              Receive items
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard
          label="Receipts (30 days)"
          value={stats.receipts.toLocaleString()}
          loading={isLoading}
        />
        <MetricCard
          label="Units received (30 days)"
          value={stats.units.toLocaleString()}
          loading={isLoading}
        />
        <MetricCard
          label="Value received (30 days)"
          value={formatCurrency(stats.value, { whole: true })}
          loading={isLoading}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<ReceivingLineRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(row) => (canManage ? setEditing(row) : printReceipt(row, rows))}
            rowClassName={(r) => (r.status === 'reversed' ? 'opacity-60' : undefined)}
            searchPlaceholder="Search by receipt #, PO #, vendor, SKU, or item…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="procurement-receiving"
            enableColumnResizing
            mobileCard={{
              title: (r) => receivingLineName(r),
              subtitle: (r) => (
                <span className="font-mono text-[11px]">
                  {r.goods_receipts?.receipt_number} ·{' '}
                  {r.goods_receipts?.purchase_orders?.po_number}
                </span>
              ),
              badges: (r) => [
                r.status === 'reversed' ? (
                  <Badge key="status" variant="muted">
                    Reversed
                  </Badge>
                ) : null,
                r.locations?.name ? (
                  <Badge key="loc" variant="secondary">
                    {r.locations.name}
                  </Badge>
                ) : null,
              ],
              amount: (r) => ({
                value: receivedQtyLabel(r),
                tone: 'neutral',
                caption: formatCurrency(extendedCost(r)),
              }),
              meta: (r) => [
                { label: 'Received', value: formatDate(r.goods_receipts?.received_at) },
                { label: 'Vendor', value: r.goods_receipts?.purchase_orders?.vendors?.name ?? '—' },
              ],
            }}
            emptyState={
              <EmptyState
                icon={PackageCheck}
                title="Nothing received yet"
                action={
                  canManage ? (
                    <Button onClick={() => setReceiveOpen(true)}>
                      <PackageCheck className="h-4 w-4" />
                      Receive items
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && (
        <>
          <ReceiveDialog open={receiveOpen} onOpenChange={setReceiveOpen} />
          <EditReceivingDialog
            open={editing !== null}
            onOpenChange={(o) => !o && setEditing(null)}
            line={editing}
            onPrint={(line) => printReceipt(line, rows)}
          />
        </>
      )}
    </div>
  );
}
